<template>
  <div>    
    <!-- Uso de cada IL del Programa a lo largo del diseño, agrupado por su Unidad de
         Estudio. Antes esta tabla listaba sólo los IL que nadie trabajaba: decía qué
         faltaba pero no dónde estaba lo demás. Actividad y evaluación van en columnas
         separadas a propósito: un IL trabajado en clase pero que ninguna evaluación
         mide es un hallazgo distinto de uno que se evalúa sin haberse trabajado.
         La cabecera del RA es la que se expande, con la triangulación contra las
         Estrategias Metodológicas — que son de ese nivel, no del IL. -->
    <b-table-simple small responsive class="mb-2">
      <b-thead>
        <b-tr>
          <b-th
            class="text-align-center evaluation-header kl-panel-title py-2"
            colspan="3"
          >
            {{
              $t("teaching.instructional.coverageUsageTitle", {
                entity: indicatorEntityName,
              }).toUpperCase()
            }}
            <!-- E48 · El análisis IA de la triangulación, como en la Matriz 2. Va en la
                 cabecera de la tabla y no en cada Unidad: la triangulación se juzga
                 cruzando Unidades, y un botón por fila gastaría el límite de reportes por
                 hora en una sola pasada. El reporte trae una sección por Unidad adentro,
                 que es el mismo detalle sin ese costo. -->
            <b-button
              v-if="showsTriangulationAi"
              v-b-tooltip.topleft.v-secondary.noninteractive="{
                title: $t('teaching.instructional.triangulationAiTooltip'),
                boundary: 'viewport',
              }"
              size="sm"
              variant="none"
              class="btn-ai-custom kl-triangulation-ai"
              @click="openTriangulationReport"
            >
              <img src="@/assets/icon-ai.svg" alt="" class="icon-btn-ai mr-1" />
              {{ $t("teaching.instructional.triangulationAiButton") }}
            </b-button>
          </b-th>
        </b-tr>
        <b-tr v-if="indicators.length > 0">
          <b-th class="text-align-center evaluation-header py-2">
            {{ indicatorEntityName.toUpperCase() }}
          </b-th>
          <b-th class="text-align-center evaluation-header py-2 usage-column">
            {{ activityEntityNamePlural.toUpperCase() }}
          </b-th>
          <b-th class="text-align-center evaluation-header py-2 usage-column">
            {{ evaluationEntityNamePlural.toUpperCase() }}
          </b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr v-if="indicators.length == 0">
          <b-td class="text-center coverage-empty" colspan="3">
            {{
              $t("teaching.instructional.coverageNoIndicators", {
                entity: indicatorEntityName,
                matter: matterEntityName,
              })
            }}
          </b-td>
        </b-tr>
        <template v-for="group in unitGroups">
          <b-tr
            :key="`unit-${group.id}`"
            class="unit-row"
            :class="{ 'unit-row-clickable': !group.unused }"
          >
            <b-td
              colspan="3"
              @click="group.unused ? null : toggleUnit(group.id)"
            >
              <div class="d-flex align-items-center">
                <span v-if="!group.unused" class="usage-toggle">
                  <b-icon
                    :icon="
                      isUnitExpanded(group.id)
                        ? 'chevron-down'
                        : 'chevron-right'
                    "
                    scale="0.9"
                  ></b-icon>
                </span>
                <span v-else class="usage-toggle-spacer"></span>
                <span
                  class="unit-title"
                  :class="{ 'text-danger': group.unused }"
                >
                  {{ group.label }}. {{ group.name }}
                </span>
                <!-- Que la fila se abre sólo se sabía pasando el mouse por encima: el aviso
                     iba en un tooltip de la fila entera y quedaba escondido. Acá se ve que
                     hay algo que leer antes de buscarlo. -->
                <InfoTooltip
                  v-if="!group.unused"
                  class="ml-2"
                  :tooltip_text="
                    $t('teaching.instructional.coverageUsageExpand')
                  "
                ></InfoTooltip>
              </div>
            </b-td>
          </b-tr>
          <b-tr
            v-if="isUnitExpanded(group.id)"
            :key="`coherence-${group.id}`"
            class="coherence-row"
          >
            <b-td colspan="3">
              <div class="coherence-box">
                <div
                  v-if="group.evaluated_not_taught"
                  class="coherence-flag coherence-flag-danger"
                >
                  <b-icon icon="exclamation-triangle-fill" scale="0.9"></b-icon>
                  {{
                    $t("teaching.instructional.coherenceEvaluatedNotTaught", {
                      entity: studyUnitEntityNameSingular,
                      activity: activityEntityName,
                    })
                  }}
                </div>
                <div
                  v-if="group.taught_not_evaluated"
                  class="coherence-flag coherence-flag-danger"
                >
                  <b-icon icon="exclamation-triangle-fill" scale="0.9"></b-icon>
                  {{
                    $t("teaching.instructional.coherenceTaughtNotEvaluated", {
                      entity: studyUnitEntityNameSingular,
                      evaluation: evaluationEntityName,
                    })
                  }}
                </div>
                <div
                  v-if="group.shared_strategies.length > 0"
                  class="coherence-flag coherence-flag-ok"
                >
                  <b-icon icon="check-circle-fill" scale="0.9"></b-icon>
                  {{
                    $t("teaching.instructional.coherenceSharedStrategies", {
                      names: group.shared_strategies.join(", "),
                    })
                  }}
                </div>
                <div
                  v-else-if="group.comparable"
                  class="coherence-flag coherence-flag-warning"
                >
                  <b-icon icon="question-circle-fill" scale="0.9"></b-icon>
                  {{ $t("teaching.instructional.coherenceNoSharedStrategies") }}
                </div>
                <div
                  v-if="group.shared_evidences.length > 0"
                  class="coherence-flag coherence-flag-ok"
                >
                  <b-icon icon="check-circle-fill" scale="0.9"></b-icon>
                  {{
                    $t("teaching.instructional.coherenceSharedEvidences", {
                      names: group.shared_evidences.join(", "),
                    })
                  }}
                </div>
                <div
                  v-else-if="group.evidences_comparable"
                  class="coherence-flag coherence-flag-warning"
                >
                  <b-icon icon="question-circle-fill" scale="0.9"></b-icon>
                  {{
                    $t("teaching.instructional.coherenceNoSharedEvidences", {
                      evaluation: evaluationEntityName,
                    })
                  }}
                </div>

                <!-- E49 · Qué momentos del modelo recorre esta Unidad. La distribución del
                     pie de la tabla dice lo mismo para el diseño entero, y el aviso del
                     módulo avisa cuando la secuencia se corta; acá la pregunta es más
                     chica y más concreta: con qué se enseña y se mide ESTE aprendizaje.
                     Va arriba de las dos columnas porque es lo que las encuadra. -->
                <div v-if="group.moments.length > 0" class="kl-unit-moments">
                  <span class="coherence-row-label">
                    {{ $t("teaching.instructional.coherenceMoments") }}
                  </span>
                  <span
                    v-for="item in group.moments"
                    :key="`coherence-m-${group.id}-${item.key}`"
                    class="coherence-chip kl-unit-moment-chip"
                    :class="{
                      'kl-unit-moment-loose': item.loose,
                      'kl-unit-moment-empty': item.empty,
                    }"
                  >
                    <!-- El color del momento sólo se pinta cuando la Unidad lo trabaja: el
                         punto relleno dice «esto está acá». El vacío lo deja en rojo por
                         CSS, y por eso acá no va ningún color inline que lo tape. -->
                    <span
                      class="moment-dot kl-unit-moment-dot"
                      :style="
                        item.empty ? {} : { backgroundColor: item.color }
                      "
                    ></span>
                    {{ item.name }}
                    <span class="kl-unit-moment-count">{{ item.count }}</span>
                  </span>
                </div>

                <div class="coherence-columns">
                  <div class="coherence-side">
                    <div class="coherence-side-title">
                      {{ $t("teaching.instructional.coherenceTaughtWith") }}
                    </div>
                    <div
                      v-for="(piece, index) in group.activities"
                      :key="`coherence-a-${group.id}-${index}`"
                      class="coherence-piece"
                    >
                      <div class="coherence-piece-name">{{ piece.name }}</div>
                      <div class="coherence-piece-row">
                        <span class="coherence-row-label">
                          {{ strategyEntityNamePlural }}:
                        </span>
                        <template v-if="piece.strategies.length > 0">
                          <span
                            v-for="(strategy, s_index) in piece.strategies"
                            :key="`coherence-a-s-${group.id}-${index}-${s_index}`"
                            class="coherence-chip"
                            :class="{
                              'coherence-chip-shared':
                                group.shared_strategies.includes(strategy),
                            }"
                          >
                            {{ strategy }}
                          </span>
                        </template>
                        <span v-else class="coherence-missing">
                          {{
                            $t("teaching.instructional.coherenceNoStrategies")
                          }}
                        </span>
                      </div>
                      <div class="coherence-piece-row">
                        <span class="coherence-row-label">
                          {{ evidenceEntityNamePlural }}:
                        </span>
                        <template v-if="piece.evidences.length > 0">
                          <span
                            v-for="(evidence, e_index) in piece.evidences"
                            :key="`coherence-a-e-${group.id}-${index}-${e_index}`"
                            class="coherence-chip"
                            :class="{
                              'coherence-chip-shared':
                                group.shared_evidences.includes(evidence),
                            }"
                          >
                            {{ evidence }}
                          </span>
                        </template>
                        <span v-else class="coherence-missing">
                          {{
                            $t("teaching.instructional.coherenceNoEvidences")
                          }}
                        </span>
                      </div>
                    </div>
                    <span
                      v-if="group.activities.length == 0"
                      class="coherence-none"
                    >
                      —
                    </span>
                  </div>

                  <div class="coherence-side">
                    <div class="coherence-side-title">
                      {{ $t("teaching.instructional.coherenceEvaluatedWith") }}
                    </div>
                    <div
                      v-for="(piece, index) in group.evaluations"
                      :key="`coherence-e-${group.id}-${index}`"
                      class="coherence-piece"
                    >
                      <div class="coherence-piece-name">{{ piece.name }}</div>
                      <div class="coherence-piece-row">
                        <span class="coherence-row-label">
                          {{ strategyEntityNamePlural }}:
                        </span>
                        <template v-if="piece.strategies.length > 0">
                          <span
                            v-for="(strategy, s_index) in piece.strategies"
                            :key="`coherence-e-s-${group.id}-${index}-${s_index}`"
                            class="coherence-chip"
                            :class="{
                              'coherence-chip-shared':
                                group.shared_strategies.includes(strategy),
                            }"
                          >
                            {{ strategy }}
                          </span>
                        </template>
                        <span v-else class="coherence-missing">
                          {{
                            $t("teaching.instructional.coherenceNoStrategies")
                          }}
                        </span>
                      </div>
                      <div class="coherence-piece-row">
                        <span class="coherence-row-label">
                          {{ evidenceEntityNamePlural }}:
                        </span>
                        <template v-if="piece.evidences.length > 0">
                          <span
                            v-for="(evidence, e_index) in piece.evidences"
                            :key="`coherence-e-e-${group.id}-${index}-${e_index}`"
                            class="coherence-chip"
                            :class="{
                              'coherence-chip-shared':
                                group.shared_evidences.includes(evidence),
                            }"
                          >
                            {{ evidence }}
                          </span>
                        </template>
                        <span v-else class="coherence-missing">
                          {{
                            $t("teaching.instructional.coherenceNoEvidences")
                          }}
                        </span>
                      </div>
                    </div>
                    <span
                      v-if="group.evaluations.length == 0"
                      class="coherence-none"
                    >
                      —
                    </span>
                  </div>
                </div>
              </div>
            </b-td>
          </b-tr>

          <b-tr
            v-for="row in group.rows"
            :key="`usage-${row.indicator.id}`"
            :class="{ 'usage-unused-row': row.unused }"
          >
            <b-td :class="{ 'text-danger': row.unused }">
              <div class="ml-1 mt-1 indicator-cell">
                <SentenceContainer
                  :unique_id="`coverage-usage-${row.indicator.id}`"
                  :showColorCognitiveLevel="true"
                  :Sentence="row.indicator"
                  :boundedTextContainer="false"
                  :single_line="true"
                >
                  <template v-slot:order>
                    <span>{{ row.indicator.code }}</span>
                  </template>
                </SentenceContainer>
              </div>
            </b-td>

            <b-td class="text-center usage-column">
              <span
                v-if="row.activities.length > 0"
                :id="`usage-activities-${row.indicator.id}`"
                class="helper"
              >
                <b-badge class="border" variant="light">
                  {{ row.activities.length }}
                </b-badge>
              </span>
              <span
                v-else
                class="usage-none"
                :class="{ 'usage-none-unused': row.unused }"
                >0</span
              >
              <b-popover
                v-if="row.activities.length > 0"
                :target="`usage-activities-${row.indicator.id}`"
                placement="left"
                triggers="hover click"
                variant="dark"
              >
                <div>
                  <strong>{{ activityEntityNamePlural }}:</strong>
                  <li
                    v-for="(piece, index) in row.activities"
                    :key="`usage-activity-${row.indicator.id}-${index}`"
                  >
                    {{ piece.name }}
                  </li>
                </div>
              </b-popover>
            </b-td>

            <b-td class="text-center usage-column">
              <span
                v-if="row.evaluations.length > 0"
                :id="`usage-evaluations-${row.indicator.id}`"
                class="helper"
              >
                <b-badge class="border" variant="light">
                  {{ row.evaluations.length }}
                </b-badge>
              </span>
              <span
                v-else
                class="usage-none"
                :class="{ 'usage-none-unused': row.unused }"
                >0</span
              >
              <b-popover
                v-if="row.evaluations.length > 0"
                :target="`usage-evaluations-${row.indicator.id}`"
                placement="left"
                triggers="hover click"
                variant="dark"
              >
                <div>
                  <strong>{{ evaluationEntityNamePlural }}:</strong>
                  <li
                    v-for="(piece, index) in row.evaluations"
                    :key="`usage-evaluation-${row.indicator.id}-${index}`"
                  >
                    {{ piece.name }}
                  </li>
                </div>
              </b-popover>
            </b-td>
          </b-tr>
        </template>
      </b-tbody>
    </b-table-simple>
    <!-- E39 · Estado del Diseño. Las tablas de arriba dicen DÓNDE está cada cosa; ésta dice
         de qué tamaño es el diseño y cuánto está cubierto, que es lo que se mira primero.
         Va en dos bloques a propósito: lo que HAY y recién después lo que falta. -->
    <div class="kl-state mt-3 mb-3">
      <div class="kl-state-title">
        {{ $t("teaching.instructional.statsTitle") }}
      </div>

      <div class="kl-state-block">
        <div class="kl-state-subtitle">
          {{ $t("teaching.instructional.statsNumbersTitle") }}
        </div>
        <div class="kl-stat-row">
          <div
            v-for="row in designNumbers"
            :key="`stat-${row.key}`"
            class="kl-stat"
          >
            <div class="kl-stat-label">{{ row.label }}</div>
            <div class="kl-stat-value">{{ row.value }}</div>
            <div
              v-if="row.note"
              class="kl-stat-note"
              :class="row.note_status ? `kl-stat-note-${row.note_status}` : ''"
            >
              <!-- El estado nunca viaja sólo en el color: lleva su ícono y su texto. -->
              <b-icon
                v-if="row.note_status === 'warning'"
                icon="exclamation-triangle-fill"
                scale="0.75"
              ></b-icon>
              <b-icon
                v-else-if="row.note_status === 'danger'"
                icon="x-circle-fill"
                scale="0.75"
              ></b-icon>
              {{ row.note }}
            </div>
            <div
              v-else-if="row.note_status === 'success'"
              class="kl-stat-note kl-stat-note-success"
            >
              <b-icon icon="check-circle-fill" scale="0.75"></b-icon>
              {{ $t("teaching.instructional.statsOk") }}
            </div>
          </div>
        </div>
      </div>

      <div class="kl-state-block">
        <div class="kl-state-subtitle">
          {{ $t("teaching.instructional.statsCoverageTitle") }}
        </div>
        <div
          v-for="meter in coverageMeters"
          :key="`meter-${meter.key}`"
          class="kl-meter"
        >
          <div class="kl-meter-head">
            <span class="kl-meter-label">
              {{ meter.label }}
              <b-icon
                v-if="meter.detail"
                icon="info-circle"
                scale="0.75"
                class="kl-meter-info"
                v-b-tooltip.v-secondary.noninteractive.hover="meter.detail"
              ></b-icon>
            </span>
            <!-- La cifra acompaña siempre a la barra: la razón n/N es lo que deja verificar
                 el porcentaje sin tener que estimarlo del largo del relleno. -->
            <span class="kl-meter-figures">
              <span
                v-if="meter.missing.length > 0"
                :id="`meter-missing-${meter.key}`"
                class="helper kl-meter-percentage"
              >
                {{ meter.percentage | Round }}%
              </span>
              <span v-else class="kl-meter-percentage">
                {{ meter.percentage | Round }}%
              </span>
              <span class="kl-meter-ratio">
                {{ meter.covered }}/{{ meter.total }}
              </span>
              <b-popover
                v-if="meter.missing.length > 0"
                :target="`meter-missing-${meter.key}`"
                placement="left"
                triggers="hover"
                variant="dark"
              >
                <div class="kl-meter-popover">
                  <div class="kl-meter-popover-title">
                    {{ $t("teaching.instructional.statsMissingTitle") }}
                  </div>
                  <div
                    v-for="(item, index) in meter.missing"
                    :key="`${meter.key}-missing-${index}`"
                  >
                    <span
                      v-if="item.color"
                      class="moment-dot"
                      :style="{ backgroundColor: item.color }"
                    ></span>
                    {{ item.label }}
                  </div>
                </div>
              </b-popover>
            </span>
          </div>
          <!-- Meter: el relleno carga el estado y el tramo vacío va en un paso más claro
               del MISMO color, no en gris, para que el estado se lea a lo largo de toda la
               barra. Sin datos que medir, la barra queda neutra. -->
          <div class="kl-meter-track" :class="`kl-meter-${meter.status}`">
            <div
              class="kl-meter-fill"
              :style="{ width: `${meter.total > 0 ? meter.percentage : 0}%` }"
            ></div>
          </div>
        </div>
      </div>

      <!-- E46 · La distribución por momento, al final y como bloque propio.
           Reemplaza al medidor "Momentos con al menos una pieza", que contestaba la misma
           pregunta en binario: un momento vacío es acá una fila en cero, y eso ya decía el
           medidor completo. Y va a la vista, no en el popover "Faltan:", porque el
           desbalance se lee COMPARANDO —diez piezas en Activación contra una en
           Integración cubre todos los momentos y no recorre ninguno— y lo que hay que
           comparar no puede estar escondido detrás del mouse. -->
      <div v-if="momentDistribution.length > 0" class="kl-state-block">
        <div class="kl-state-subtitle">
          {{
            $t("teaching.instructional.statsMomentsDistribution", {
              entity: piecesEntityName,
            })
          }}
        </div>
        <div
          v-for="item in momentDistribution"
          :key="`dist-${item.key}`"
          class="kl-moment-dist-row"
        >
          <span
            class="kl-moment-dist-name"
            :class="{ 'kl-moment-dist-loose': item.loose }"
          >
            <span
              class="moment-dot kl-moment-dist-dot"
              :style="{ backgroundColor: item.color }"
            ></span>
            {{ item.name }}
          </span>
          <!-- El total va pegado al nombre y no junto al desglose: son dos cifras de la
               misma fila y, una al lado de la otra, el "10" se leía como parte del "8 y
               2". La barra queda en medio separándolas. -->
          <span class="kl-moment-dist-count">{{ item.count }}</span>
          <span class="kl-moment-dist-track">
            <span
              class="kl-moment-dist-fill"
              :style="{
                width: `${distributionWidth(item.count)}%`,
                backgroundColor: item.color,
              }"
            ></span>
          </span>
          <!-- El desglose a la vista y no en un tooltip: saber que son 10 no dice si el
               momento se trabaja o si sólo se mide, y eso es lo que se viene a leer. -->
          <span class="kl-moment-dist-split">{{ item.split }}</span>
        </div>
      </div>
    </div>

    <!-- `show_button=false`: el disparador es el de la cabecera, para que quede junto al
         título de lo que analiza. El modal se monta fuera de la tabla porque mide xl y no
         tiene por qué heredar nada de su scroll horizontal. -->
    <AiReportModal
      v-if="showsTriangulationAi"
      ref="triangulation_report_modal"
      :show_button="false"
      :reportConfig="{
        inteligence_option: 9,
        title: $t('teaching.instructional.triangulationAiTitle'),
      }"
      :egress_profile_id="egress_profile_id"
      :egress_profile_matter_id="egress_profile_matter_id"
      :matter_base_id="egress_profile_matter_id ? null : matter_base_id"
      :subtitle="matter_name"
    ></AiReportModal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as names from "@/store/names";
// E37 · El tipo 4 es la "Actividad": lo que se trabaja, frente a lo que se mide.
import { WRAPPER_TYPE_ACTIVITY } from "@/utils/instructional_activity_types";

export default {
  name: "InstructionalCoverageTable",
  components: {
    SentenceContainer: () => import("@/components/reusable/SentenceContainer"),
    InfoTooltip: () => import("@/components/reusable/InfoTooltip"),
    AiReportModal: () => import("@/components/reusable/AiReportModal"),
  },
  props: {
    // Actividades del diseño (dentro y fuera de un Modulo).
    activities: {
      type: Array,
      default: () => [],
    },
    // Momentos del método que eligió la Asignatura, en orden.
    moments: {
      type: Array,
      default: () => [],
    },
    // StudyUnits de la Asignatura (Programa).
    // E39 · Tamaño y forma del diseño, calculados por el orquestador: las horas, las semanas
    // y el nivel viven allá. Va como objeto y no como seis props sueltas porque se consume
    // entero y crece junto.
    design_stats: {
      type: Object,
      default: () => ({}),
    },
    study_units: {
      type: Array,
      default: () => [],
    },
    // E48 · Los dos ids que necesita el reporte IA. Vienen del orquestador y no se
    // deducen acá: el nivel `matter` del diseño no tiene Perfil de Egreso, y es
    // justamente el caso en el que el botón no debe aparecer.
    egress_profile_id: {
      type: Number,
      default: null,
    },
    egress_profile_matter_id: {
      type: Number,
      default: null,
    },
    // E52 · La Asignatura base. Es el nivel del reporte cuando el diseño se abrió por
    // ella y no por un plan.
    matter_base_id: {
      type: Number,
      default: null,
    },
    matter_name: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      WRAPPER_TYPE_ACTIVITY: WRAPPER_TYPE_ACTIVITY,
      // Qué Unidades de Estudio están abiertas. Array y no Set: en Vue 2 un Set no
      // es reactivo.
      expanded_units: [],
    };
  },
  computed: {
    studyUnitEntityName() {
      return this.$getVisibleNames(
        "teaching.studyunit",
        false,
        "Unidad de Estudio"
      );
    },
    /**
     * E48 · Quién ve el botón de análisis IA.
     *
     * Mismo gating que la Matriz 2 —el componente de IA del usuario— más el id del
     * nivel sobre el que se pide el reporte.
     *
     * E52 · Antes exigía Perfil de Egreso y Asignatura del Perfil, así que en el diseño
     * de la Asignatura base el botón no aparecía: el endpoint rechazaba la petición sin
     * Perfil. Ahora acepta los dos niveles, y lo que hace falta es uno de los dos pares:
     * Perfil + Asignatura del Perfil, o la Asignatura base sola.
     *
     * La tabla entera ya se muestra sólo a quien puede editar el diseño
     * (`v-if=\"allows_crud\"` en `Instructional.vue`), así que eso no se repite acá.
     */
    showsTriangulationAi() {
      const tieneNivel =
        (this.egress_profile_id && this.egress_profile_matter_id) ||
        this.matter_base_id;
      if (!tieneNivel) return false;
      if (!this.user) return false;
      return (
        this.user.user_data == null ||
        (this.user.user_data && this.user.user_data.ai_component)
      );
    },
    ...mapGetters({
      user: "getUser",
      evaluationCriteriasMicros: names.EVALUATION_CRITERIAS,
      competence_units: names.COMPETENCE_UNITS,
      methodologicalStrategies: names.METHODOLOGICAL_STRATEGIES,
      procedureEvidences: names.PROCEDURE_EVIDENCES,
    }),
    indicatorEntityName() {
      return this.$getVisibleNames(
        "teaching.evaluationcriteriamicro",
        true,
        "Indicadores de Logro"
      );
    },
    studyUnitEntityNameSingular() {
      return this.$getVisibleNames(
        "teaching.studyunit",
        false,
        "Unidad de Estudio"
      );
    },
    matterEntityName() {
      return this.$getVisibleNames(
        "mesh.egressprofilematter",
        false,
        "Asignatura"
      );
    },
    activityEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.activity",
        false,
        "Actividad"
      );
    },
    evaluationEntityName() {
      return this.$getVisibleNames(
        "evaluations2.evaluation",
        false,
        "Evaluación"
      );
    },
    studyUnitEntityNamePlural() {
      return this.$getVisibleNames(
        "teaching.studyunit",
        true,
        "Unidades de Estudio"
      );
    },
    // Las dos cosas juntas: el resumen cuenta piezas del diseño sin distinguir de qué lado
    // del triángulo están, a diferencia de la tabla de arriba, que existe para separarlas.
    piecesEntityName() {
      return `${this.activityEntityNamePlural} ${this.$t(
        "teaching.instructional.statsAnd"
      )} ${this.evaluationEntityNamePlural}`;
    },
    activityEntityNamePlural() {
      return this.$getVisibleNames(
        "manual.instructional_design.activity",
        true,
        "Actividades"
      );
    },
    strategyEntityNamePlural() {
      return this.$getVisibleNames(
        "mesh.methodologicalstrategy",
        true,
        "Estrategias Metodológicas"
      );
    },
    evidenceEntityNamePlural() {
      return this.$getVisibleNames(
        "manual.evidencia_de_desempeño",
        true,
        "Evidencias de Desempeño"
      );
    },
    evaluationEntityNamePlural() {
      return this.$getVisibleNames(
        "evaluations2.evaluation",
        true,
        "Evaluaciones"
      );
    },
    // IL de la Asignatura con el formato de orden del Programa (N°[UC.]RA.IL).
    indicators() {
      const study_unit_by_id = new Map(
        this.study_units.map((study_unit) => [study_unit.id, study_unit])
      );
      return this.evaluationCriteriasMicros
        .filter((x) => study_unit_by_id.has(x.study_unit))
        .map((indicator) => {
          const study_unit = study_unit_by_id.get(indicator.study_unit);
          const study_unit_label = `${this.getCompetenceUnitOrder(
            study_unit.temp_competence_unit
          )}${study_unit.order}`;
          // Se conserva el IL completo para que SentenceContainer lo interprete.
          return {
            ...indicator,
            // El orden de la UC va aparte del de la Unidad: ordenar sólo por el de
            // la Unidad mezclaba las UC (1.1, 2.1, 3.1, 1.2...).
            competence_unit_order: this.getCompetenceUnitOrderValue(
              study_unit.temp_competence_unit
            ),
            study_unit_order: study_unit.order,
            study_unit_name: study_unit.name,
            study_unit_label,
            code: `${study_unit_label}.${indicator.order}`,
          };
        })
        .sort(
          (a, b) =>
            a.competence_unit_order - b.competence_unit_order ||
            a.study_unit_order - b.study_unit_order ||
            a.order - b.order
        );
    },
    // IL usados por al menos una ficha del diseño.
    //
    // E24: incluye los de las evaluaciones referenciadas, que declaran sus IL en
    // el Programa y no en la ficha. Sin esto la tabla marcaba como "no cubierto"
    // un IL que sí evalúa la Sumativa de la unidad, que es precisamente el vacío
    // que E24 vino a cerrar.
    coveredIndicatorIds() {
      const ids = new Set();
      this.activities.forEach((activity) => {
        (activity.evaluation_criterias_micro || []).forEach((id) =>
          ids.add(id)
        );
      });
      return ids;
    },
    uncoveredIndicators() {
      return this.indicators.filter(
        (indicator) => !this.coveredIndicatorIds.has(indicator.id)
      );
    },
    // Dónde se ocupa cada IL, separado en las dos cosas que conviven en el diseño
    // (E24): las Actividades, que declaran sus IL en la ficha, y las evaluaciones
    // referenciadas, que los declaran en el Programa. Una sola pasada por las
    // fichas, indexada por IL: recorrerlas por cada fila era cuadrático.
    indicatorUsage() {
      const usage = new Map();
      const add = (id, key, piece) => {
        if (!usage.has(id)) usage.set(id, { activities: [], evaluations: [] });
        usage.get(id)[key].push(piece);
      };
      this.activities.forEach((activity) => {
        const { key, ids, piece } = this.readPiece(activity);
        ids.forEach((indicator_id) => add(indicator_id, key, piece));
      });
      return usage;
    },
    // De qué Unidad es cada IL. Se arma una vez y la usan las dos agregaciones.
    unitByIndicatorId() {
      const by_indicator = new Map();
      this.indicators.forEach((indicator) =>
        by_indicator.set(indicator.id, indicator.study_unit)
      );
      return by_indicator;
    },
    // Las piezas que tocan cada Unidad. Una actividad que cubre tres IL del mismo RA
    // entra **una sola vez**: es justo la repetición que tenía la versión por IL.
    unitUsage() {
      const usage = new Map();
      this.activities.forEach((activity) => {
        const { key, ids, piece } = this.readPiece(activity);
        const units = new Set();
        ids.forEach((indicator_id) => {
          const unit_id = this.unitByIndicatorId.get(indicator_id);
          if (unit_id != null) units.add(unit_id);
        });
        units.forEach((unit_id) => {
          if (!usage.has(unit_id))
            usage.set(unit_id, { activities: [], evaluations: [] });
          usage.get(unit_id)[key].push(piece);
        });
      });
      return usage;
    },
    // Una entrada por Unidad de Estudio, con sus IL dentro y la triangulación ya
    // resuelta. Sólo salen las Unidades que tienen IL: una sin ellos no tiene nada
    // que mostrar en esta tabla.
    unitGroups() {
      const groups = [];
      const by_unit = new Map();
      this.indicatorRows.forEach((row) => {
        const unit_id = row.indicator.study_unit;
        if (!by_unit.has(unit_id)) {
          const group = {
            id: unit_id,
            name: row.indicator.study_unit_name,
            label: row.indicator.study_unit_label,
            rows: [],
          };
          by_unit.set(unit_id, group);
          groups.push(group);
        }
        by_unit.get(unit_id).rows.push(row);
      });
      return groups.map((group) => {
        const usage = this.unitUsage.get(group.id) || {
          activities: [],
          evaluations: [],
        };
        const pieces = usage.activities.concat(usage.evaluations);
        // Las estrategias que aparecen a los dos lados. Se comparan por nombre y no
        // por id porque es lo que el usuario lee, y dos estrategias con el mismo
        // nombre son la misma para efectos de esta lectura.
        const shared = this.sharedNames(
          usage.activities,
          usage.evaluations,
          "strategies"
        );
        // E36 · La evidencia que la actividad produce y la evaluación recoge es la misma.
        // A diferencia del cruce de estrategias, acá el match es literal y no interpreta
        // nada: el estudiante escribe un Ensayo en clase y la Sumativa evalúa un Ensayo.
        // Es el hecho más fuerte que esta tabla puede afirmar hoy.
        const shared_evidences = this.sharedNames(
          usage.activities,
          usage.evaluations,
          "evidences"
        );
        // "Sin estrategias en común" sólo tiene sentido preguntarlo cuando los dos
        // lados existen y los dos declararon algo: si falta un lado, el hallazgo es
        // el hueco, no la comparación.
        const comparable =
          usage.activities.some((piece) => piece.strategies.length > 0) &&
          usage.evaluations.some((piece) => piece.strategies.length > 0);
        return Object.assign({}, group, {
          activities: usage.activities,
          evaluations: usage.evaluations,
          unused: pieces.length == 0,
          evaluated_not_taught:
            usage.evaluations.length > 0 && usage.activities.length == 0,
          taught_not_evaluated:
            usage.activities.length > 0 && usage.evaluations.length == 0,
          shared_strategies: shared,
          shared_evidences: shared_evidences,
          moments: this.unitMoments(usage.activities, usage.evaluations),
          // El espejo de `comparable` para las evidencias: preguntarse si coinciden sólo
          // tiene sentido cuando los dos lados declararon alguna.
          evidences_comparable:
            usage.activities.some((piece) => piece.evidences.length > 0) &&
            usage.evaluations.some((piece) => piece.evidences.length > 0),
          comparable,
        });
      });
    },
    indicatorRows() {
      return this.indicators.map((indicator) => {
        const usage = this.indicatorUsage.get(indicator.id) || {
          activities: [],
          evaluations: [],
        };
        // El IL conserva su conteo propio: la Unidad puede estar cubierta y un IL
        // suyo no. La triangulación, en cambio, vive en el grupo (`unitGroups`).
        return {
          indicator,
          activities: usage.activities,
          evaluations: usage.evaluations,
          unused: usage.activities.length + usage.evaluations.length == 0,
        };
      });
    },
    // E39 · "El diseño en números": lo que HAY, antes de lo que falta.
    //
    // La versión anterior de esta tabla mostraba sólo carencias —cada cifra era algo que
    // faltaba—, y eso no es un resumen sino un reporte de errores. Primero hay que poder
    // decir de qué tamaño es el diseño; el hueco se entiende después, contra ese tamaño.
    designNumbers() {
      const stats = this.design_stats || {};
      const activities = this.activities.filter(
        (x) => x.type == this.WRAPPER_TYPE_ACTIVITY
      ).length;
      const rows = [
        {
          key: "pieces",
          label: this.piecesEntityName,
          value: this.activities.length,
          // El desglose va al lado y no en filas aparte: es el mismo total, partido.
          note: this.$t("teaching.instructional.statsPiecesSplit", {
            activities: activities,
            evaluations: this.activities.length - activities,
            activity_label: this.activityEntityNamePlural,
            evaluation_label: this.evaluationEntityNamePlural,
          }),
        },
      ];

      // Horas y semanas sólo aparecen si la Asignatura las tiene cargadas. Sin presupuesto
      // no hay divisor, y una fila que dice "0 de 0" no informa nada.
      if (stats.total_hours > 0) {
        rows.push({
          key: "hours",
          label: this.$t("teaching.instructional.statsHours"),
          value: this.$t("teaching.instructional.statsOutOf", {
            used: this.round(stats.used_hours),
            total: this.round(stats.total_hours),
          }),
          note: `${this.round((stats.used_hours / stats.total_hours) * 100)}%`,
          // Pasarse del presupuesto de la Asignatura es un problema, no un detalle.
          note_status: stats.used_hours > stats.total_hours ? "danger" : null,
        });
      }
      if (stats.total_weeks > 0) {
        rows.push({
          key: "weeks",
          label: this.$t("teaching.instructional.statsWeeks"),
          value: this.$t("teaching.instructional.statsOutOf", {
            used: stats.occupied_weeks,
            total: stats.total_weeks,
          }),
          note_status:
            stats.occupied_weeks > stats.total_weeks ? "danger" : null,
          note:
            stats.occupied_weeks > stats.total_weeks
              ? this.$t("teaching.instructional.statsWeeksOverflow", {
                  matter: this.matterEntityName,
                })
              : null,
        });
      }

      // La ponderación es del Programa y su regla es la del Programa: 100%. El diseño la
      // muestra para no obligar a salir a otra vista a confirmarlo.
      const weighing = this.round(stats.weighing || 0);
      rows.push({
        key: "weighing",
        label: this.$t("teaching.instructional.statsWeighing"),
        value: `${weighing}%`,
        note_status: weighing == 100 ? "success" : "warning",
        note:
          weighing == 100
            ? null
            : this.$t("teaching.instructional.statsWeighingOff"),
      });
      return rows;
    },
    // E39 · La cobertura, en POSITIVO: cuánto está cubierto, no cuánto falta.
    //
    // "100% sin Evidencias" obliga a dar vuelta el número en la cabeza; "0% con Evidencias"
    // se lee de una. El hover sigue diciendo CUÁLES faltan, que es el dato accionable.
    coverageMeters() {
      const pieces = this.activities;
      const total_pieces = pieces.length;

      const pieceName = (piece) =>
        piece.name ||
        this.$t("teaching.instructional.activityUntitled", {
          entity: this.$getVisibleNames(
            "manual.instructional_design.activity",
            false,
            "Actividad"
          ),
        });
      // Sólo cuentan las Unidades con los dos lados declarando algo: si falta un lado, el
      // hallazgo es el hueco y ya lo dicen las filas de arriba, no la falta de coincidencia.
      const comparable_units = this.unitGroups.filter(
        (group) => group.comparable || group.evidences_comparable
      );
      const units_without_overlap = comparable_units.filter(
        (group) =>
          group.shared_strategies.length == 0 &&
          group.shared_evidences.length == 0
      );

      // Una pieza "completa" declara las tres cosas. Es la fila que más importa: resume en
      // un número si el diseño está realmente descrito o sólo esbozado.
      //
      // El detalle dice QUÉ le falta a cada una. Sólo el nombre obligaría a abrir las cuatro
      // para descubrir cuál era el hueco, que es justo lo que este resumen viene a evitar.
      const missing_fields = [
        ["evaluation_criterias_micro", this.indicatorEntityName],
        ["methodological_strategies", this.strategyEntityNamePlural],
        ["procedure_evidences", this.evidenceEntityNamePlural],
      ];
      const incomplete = pieces
        .map((piece) => {
          const lacks = missing_fields
            .filter(([field]) => (piece[field] || []).length == 0)
            .map(([, label]) => label);
          return lacks.length > 0
            ? `${pieceName(piece)} — ${this.$t(
                "teaching.instructional.statsLacks"
              )} ${lacks.join(", ")}`
            : null;
        })
        .filter((x) => x != null);

      /**
       * Los saltos de secuencia del método.
       *
       * Un modelo instruccional es un recorrido: no se puede enseñar desde un momento
       * avanzado si el diseño todavía no pasó por los anteriores. Se recorre el diseño en
       * su orden llevando hasta dónde llegó el método, y se marca la pieza que arranca más
       * allá de lo alcanzado más uno — ésa se saltea momentos.
       *
       * Se AVISA y no se impide: el docente puede tener una razón, y hay diseños ya
       * cargados que hoy no cumplen esto. El aviso es lo que hace visible el problema.
       */
      const order_of = {};
      this.moments.forEach((moment) => {
        order_of[moment.id] = moment.order;
      });
      const in_sequence = [...pieces].sort(
        (a, b) => a.order - b.order || a.id - b.id
      );
      const sequenced = [];
      const sequence_breaks = [];
      let reached = 0;
      in_sequence.forEach((piece) => {
        const orders = (piece.moments || [])
          .map((id) => order_of[id])
          .filter((x) => x != null);
        if (orders.length == 0) return;
        sequenced.push(piece);
        const first = Math.min(...orders);
        if (first > reached + 1) {
          const skipped = this.moments
            .filter((m) => m.order > reached && m.order < first)
            .map((m) => m.name);
          sequence_breaks.push({
            label: `${pieceName(piece)} — ${this.$t(
              "teaching.instructional.statsSequenceSkips",
              { moments: skipped.join(", ") }
            )}`,
          });
        }
        reached = Math.max(reached, ...orders);
      });

      return [
        {
          key: "indicators",
          label: this.indicatorEntityName,
          total: this.indicators.length,
          missing: this.uncoveredIndicators.map((x) => ({ label: x.code })),
        },
        // E46 · Acá vivía "Momentos del modelo con al menos una pieza". Lo reemplaza la
        // distribución del bloque de abajo: decía en binario —cubierto o vacío— lo que
        // allá es una fila en cero, y tener las dos lecturas obligaba a reconciliarlas.
        {
          key: "moments-sequence",
          label: this.$t("teaching.instructional.statsSequence"),
          total: sequenced.length,
          missing: sequence_breaks,
          detail: this.$t("teaching.instructional.statsSequenceDetail"),
        },
        {
          key: "pieces-complete",
          label: this.$t("teaching.instructional.statsPiecesComplete", {
            entity: this.piecesEntityName,
          }),
          total: total_pieces,
          missing: incomplete.map((label) => ({ label })),
          detail: this.$t("teaching.instructional.statsPiecesCompleteDetail", {
            entity1: this.indicatorEntityName,
            entity2: this.strategyEntityNamePlural,
            entity3: this.evidenceEntityNamePlural,
          }),
        },
        {
          key: "units-overlap",
          label: this.$t("teaching.instructional.statsUnitsOverlap", {
            entity: this.studyUnitEntityNamePlural,
          }),
          total: comparable_units.length,
          missing: units_without_overlap.map((group) => ({
            label: `${group.label} ${group.name}`,
          })),
          detail: this.$t("teaching.instructional.statsUnitsOverlapDetail", {
            activity: this.activityEntityName,
            evaluation: this.evaluationEntityName,
            study_unit: this.studyUnitEntityName,
            study_units: this.studyUnitEntityNamePlural,
            entity1: this.strategyEntityNamePlural,
            entity2: this.evidenceEntityNamePlural,
          }),
        },
      ].map((row) => {
        const covered = row.total - row.missing.length;
        const percentage = row.total > 0 ? (covered / row.total) * 100 : 0;
        return Object.assign({}, row, {
          covered: covered,
          percentage: percentage,
          // El color de la barra carga el estado; el número y la razón n/N lo repiten en
          // texto, así que nunca se depende sólo del color para leerlo.
          status:
            row.total == 0
              ? "empty"
              : percentage >= 90
              ? "success"
              : percentage >= 60
              ? "warning"
              : "danger",
        });
      });
    },
    /**
     * E46 · Cuántas piezas cumple cada momento, en el orden del método.
     *
     * Reemplaza al medidor "Momentos con al menos una pieza" que vivía entre los de
     * cobertura: era binario —cubierto o vacío— y por eso no distinguía un momento que
     * trabaja una sola pieza de otro que trabaja diez. Ese desbalance es lo que deja ver
     * si el diseño recorre el modelo o sólo lo toca, y un momento vacío sigue estando
     * acá: es una fila en cero.
     *
     * E30 · Cuenta la pieza que CUMPLE el momento (la M2M) y también la que sólo lo trae
     * en `moment`, por si alguna ficha se guardó fuera del serializer sin la M2M
     * sembrada. Con un modelo como Gagné, donde una pieza cumple varios eventos a la vez,
     * mirar sólo `moment` dejaba en cero momentos que el diseño sí trabaja. Una pieza que
     * cumple
     * varios momentos suma en cada uno, así que la columna NO suma el total del diseño:
     * de ahí que cada fila se lea contra el momento más cargado y no contra las piezas.
     *
     * La última fila son las piezas que no declaran ningún momento. No están en ninguna
     * de las otras y sin ella desaparecerían de la lectura sin dejar rastro, que es
     * justamente el hueco que esta distribución viene a hacer visible.
     */
    momentDistribution() {
      const split = (pieces) => {
        const activities = pieces.filter(
          (x) => x.type == this.WRAPPER_TYPE_ACTIVITY
        ).length;
        return this.$t("teaching.instructional.statsPiecesSplit", {
          activities: activities,
          evaluations: pieces.length - activities,
          activity_label: this.activityEntityNamePlural,
          evaluation_label: this.evaluationEntityNamePlural,
        });
      };
      const rows = this.moments.map((moment) => {
        const pieces = this.activities.filter(
          (x) =>
            x.moment == moment.id || (x.moments || []).includes(moment.id)
        );
        return {
          key: `moment-${moment.id}`,
          name: moment.name,
          // El color viaja con el momento, igual que en las píldoras de cada tarjeta: es
          // con el que se lo reconoce en todo el diseño.
          color: moment.color || "#6b7b79",
          count: pieces.length,
          split: split(pieces),
        };
      });
      const loose = this.activities.filter(
        (x) => !x.moment && (x.moments || []).length == 0
      );
      if (loose.length > 0) {
        rows.push({
          key: "moment-none",
          name: this.$t("teaching.instructional.momentNone"),
          color: "#b7c0bf",
          count: loose.length,
          split: split(loose),
          loose: true,
        });
      }
      return rows;
    },
    // Contra qué se mide cada barra: el momento más cargado. El total de piezas no sirve
    // de referencia porque una pieza puede cumplir varios momentos y la columna sumaría
    // más que el diseño.
    distributionMax() {
      return Math.max(0, ...this.momentDistribution.map((x) => x.count));
    },
  },
  methods: {
    openTriangulationReport() {
      if (this.$refs.triangulation_report_modal)
        this.$refs.triangulation_report_modal.openReport();
    },
    distributionWidth(count) {
      if (this.distributionMax == 0) return 0;
      return (count / this.distributionMax) * 100;
    },
    // Los nombres que aparecen a los dos lados. Se compara por nombre y no por id porque
    // es lo que el usuario lee, y dos entradas homónimas del catálogo son la misma cosa
    // para efectos de esta lectura.
    sharedNames(left_pieces, right_pieces, field) {
      const left = new Set();
      left_pieces.forEach((piece) =>
        piece[field].forEach((name) => left.add(name))
      );
      const shared = [];
      right_pieces.forEach((piece) =>
        piece[field].forEach((name) => {
          if (left.has(name) && !shared.includes(name)) shared.push(name);
        })
      );
      return shared;
    },
    // Las horas vienen con decimales del reparto por Ambiente; en el resumen estorban.
    // Se redondea a 1 decimal y se deja entero cuando no hay parte decimal.
    round(value) {
      const rounded = Math.round(Number(value || 0) * 10) / 10;
      return Number.isInteger(rounded) ? rounded : rounded.toFixed(1);
    },
    isUnitExpanded(study_unit_id) {
      return this.expanded_units.includes(study_unit_id);
    },
    toggleUnit(study_unit_id) {
      const index = this.expanded_units.indexOf(study_unit_id);
      if (index == -1) this.expanded_units.push(study_unit_id);
      else this.expanded_units.splice(index, 1);
    },
    // Lo que las dos agregaciones (por IL y por Unidad) necesitan leer de una pieza: de qué
    // lado del triángulo va, qué IL declara y con qué se la describe.
    //
    // E37 · La pieza ES la evaluación del Programa, así que el nombre y los IL salen de ella
    // sin ramas. Lo único que la clasifica es el TIPO: la del tipo 4 es la Actividad —lo que
    // se trabaja— y las otras cuatro son evaluaciones —lo que se mide—. Esa distinción es la
    // que da sentido a las dos columnas separadas de la tabla.
    readPiece(activity) {
      const is_activity = activity.type == this.WRAPPER_TYPE_ACTIVITY;
      return {
        key: is_activity ? "activities" : "evaluations",
        ids: activity.evaluation_criterias_micro || [],
        piece: {
          name:
            activity.name ||
            this.$t("teaching.instructional.activityUntitled", {
              entity: this.$getVisibleNames(
                "manual.instructional_design.activity",
                false,
                "Actividad"
              ),
            }),
          // Estrategias y Evidencias son la capa que el diseño agrega, y las declaran los
          // cinco tipos por igual: son las dos mitades de lo que esta tabla enfrenta — con
          // qué se trabaja y qué queda de ese trabajo.
          strategies: this.strategyNames(activity),
          evidences: this.evidenceNames(activity),
          // E30 · Los momentos que la pieza cumple. Se leen igual que en la distribución
          // del pie de la tabla: la M2M y, de respaldo, el `moment` suelto por si alguna
          // ficha se guardó fuera del serializer.
          moments: (activity.moments || []).length > 0
            ? activity.moments
            : activity.moment
            ? [activity.moment]
            : [],
        },
      };
    },
    // E49 · Cuántas piezas de esta Unidad cumple cada momento, en el orden del método.
    //
    // Salen TODOS los momentos del modelo, también los que la Unidad no usa: el momento
    // en cero es el hallazgo. Una lista que sólo mostrara los usados obliga a recordar de
    // memoria el modelo completo para notar cuál falta, y es exactamente lo que el
    // usuario no tiene a mano mientras lee una Unidad.
    //
    // Una pieza que cumple varios momentos suma en cada uno, así que estos números NO
    // suman las piezas de la Unidad.
    unitMoments(activities, evaluations) {
      const rows = [];
      const pieces = activities.concat(evaluations);
      // Una Unidad sin ninguna pieza daría el modelo entero en cero, y eso no es un
      // hallazgo nuevo: el aviso de la Unidad sin uso ya lo dijo con más claridad.
      if (pieces.length == 0) return rows;
      this.moments.forEach((moment) => {
        const matched = pieces.filter((piece) =>
          piece.moments.includes(moment.id)
        );
        rows.push({
          key: `moment-${moment.id}`,
          name: moment.name,
          color: moment.color || "#6b7b79",
          count: matched.length,
          empty: matched.length == 0,
        });
      });
      // Las piezas sin momento no están en ninguna fila de arriba y son justo las que
      // dejan la secuencia incompleta: nombrarlas acá es lo que las hace visibles.
      const loose = pieces.filter((piece) => piece.moments.length == 0);
      if (loose.length > 0) {
        rows.push({
          key: "moment-none",
          name: this.$t("teaching.instructional.momentNone"),
          color: "#b7c0bf",
          count: loose.length,
          loose: true,
        });
      }
      return rows;
    },
    // Nombres de las estrategias de una ficha, ordenados para que los dos lados
    // de la comparación se lean en la misma secuencia.
    strategyNames(activity) {
      const selected = activity.methodological_strategies || [];
      return this.methodologicalStrategies
        .filter((x) => selected.includes(x.id))
        .map((x) => x.name)
        .sort((a, b) => a.localeCompare(b));
    },
    // E36 · Ídem para las Evidencias de Desempeño.
    evidenceNames(activity) {
      const selected = activity.procedure_evidences || [];
      return this.procedureEvidences
        .filter((x) => selected.includes(x.id))
        .map((x) => x.name)
        .sort((a, b) => a.localeCompare(b));
    },
    // Prefijo de la Unidad de Competencia, cuando la institución las usa.
    getCompetenceUnitOrder(temp_competence_unit_id) {
      const competence_unit = this.competence_units.find(
        (x) => x.id == temp_competence_unit_id
      );
      return competence_unit ? competence_unit.order + "." : "";
    },
    // El mismo orden, como número, para ordenar las Unidades por su UC.
    getCompetenceUnitOrderValue(temp_competence_unit_id) {
      const competence_unit = this.competence_units.find(
        (x) => x.id == temp_competence_unit_id
      );
      return competence_unit ? competence_unit.order : 0;
    },
  },
};
</script>

<style scoped>
table th {
  vertical-align: middle;
  background-color: var(--kl-color-accent-medium);
  color: var(--kl-text-on-primary);
  font-size: var(--kl-table-header-fs);
  padding: 5px 5px;
}
.evaluation-header {
  color: var(--kl-text-on-primary) !important;
  background: var(--kl-color-accent-medium) !important;
  border: 1px solid var(--kl-table-border) !important;
}
/* Titula el bloque entero, no una columna: va al tamaño de título de panel y no al de
   cabecera de tabla, igual que el resumen y el Estado del Diseño. */
.kl-panel-title {
  font-size: var(--kl-fs-title-sm);
  font-weight: var(--kl-fw-semibold, 600);
}
/* El contador no crece con el título: los badges heredan el tamaño del padre (100%) y
   al subir el titular pasaba a 16px. */
.kl-panel-title .badge {
  font-size: var(--kl-fs-caption) !important;
}
table td {
  border: 1px solid var(--kl-table-border);
  text-align: left;
  vertical-align: middle;
  font-size: var(--kl-table-fs);
  padding: 2px 2px;
}
.badge {
  margin: 0.2rem;
  font-size: 100% !important;
  margin-left: 0.5rem;
  vertical-align: middle;
  border-radius: var(--kl-radius-pill);
}
.coverage-ok {
  color: #1d6b3f;
}
.coverage-empty {
  color: #97a5a3;
  font-style: italic;
}
.usage-column {
  width: 8rem;
}
/* ---------- E39 · Estado del Diseño ---------- */
.kl-state {
  border: 1px solid var(--kl-table-border);
  border-radius: var(--kl-radius-md, 6px);
  overflow: hidden;
}
.kl-state-title {
  background: var(--kl-color-accent-medium);
  color: var(--kl-text-on-primary);
  font-size: var(--kl-fs-title-sm);
  font-weight: var(--kl-fw-semibold, 600);
  padding: 6px 10px;
  text-transform: uppercase;
}
.kl-state-block {
  padding: 10px 12px;
}
.kl-state-block + .kl-state-block {
  border-top: 1px solid var(--kl-table-border);
}
.kl-state-subtitle {
  font-size: var(--kl-fs-body);
  font-weight: 600;
  color: var(--kl-text-body);
  margin-bottom: 8px;
  text-align: left;
}
/* Fila de cifras. Envuelve sola en pantallas angostas en vez de forzar scroll. */
.kl-stat-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.kl-stat {
  flex: 1 1 8.5rem;
  min-width: 8.5rem;
  border: 1px solid var(--kl-border-soft);
  border-radius: var(--kl-radius-md, 6px);
  padding: 6px 10px;
  text-align: left;
}
.kl-stat-label {
  font-size: var(--kl-fs-caption);
  color: var(--kl-text-muted);
}
/* Cifra suelta y grande: figuras proporcionales, no tabulares — `tabular-nums` da a cada
   dígito el ancho de un 0 y a este tamaño el número se ve flojo. */
.kl-stat-value {
  font-size: var(--kl-fs-title-md);
  font-weight: 600;
  line-height: 1.2;
  color: var(--kl-text-body);
}
.kl-stat-note {
  font-size: var(--kl-fs-caption);
  color: var(--kl-text-muted);
}
/* Los colores de estado del sistema están calibrados para RELLENOS, que necesitan 3:1.
   Como texto de 9pt sobre blanco no llegan a 4.5:1 (success 3.41, danger 4.35), así que el
   rótulo usa un paso más oscuro de la MISMA rampa —los dos que este componente ya tenía, en
   6.5:1— y la barra conserva el token tal cual. */
.kl-stat-note-success {
  color: #1d6b3f;
}
.kl-stat-note-warning {
  color: #8a6800;
}
.kl-stat-note-danger {
  color: #b02a37;
}
/* ---------- Barras de cobertura ---------- */
.kl-meter + .kl-meter {
  margin-top: 8px;
}
.kl-meter-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: var(--kl-fs-small);
}
.kl-meter-label {
  text-align: left;
}
.kl-meter-info {
  color: var(--kl-text-muted);
  cursor: help;
}
.kl-meter-figures {
  white-space: nowrap;
}
/* En columna de cifras sí conviene tabular: es lo que las deja alineadas entre barras. */
.kl-meter-percentage {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.kl-meter-ratio {
  color: var(--kl-text-muted);
  margin-left: 6px;
  font-variant-numeric: tabular-nums;
}
/* Barra fina; el relleno arranca pegado al origen y sólo redondea la punta que avanza. */
.kl-meter-track {
  height: 8px;
  border-radius: 0 4px 4px 0;
  margin-top: 3px;
  overflow: hidden;
}
.kl-meter-fill {
  height: 100%;
  border-radius: 0 4px 4px 0;
  transition: width 0.2s ease;
}
.kl-meter-success {
  background: var(--kl-color-success-soft);
}
.kl-meter-success .kl-meter-fill {
  background: var(--kl-color-success);
}
.kl-meter-warning {
  background: var(--kl-color-warning-soft);
}
.kl-meter-warning .kl-meter-fill {
  background: var(--kl-color-warning);
}
.kl-meter-danger {
  background: var(--kl-color-danger-soft);
}
.kl-meter-danger .kl-meter-fill {
  background: var(--kl-color-danger);
}
/* Sin nada que medir la barra queda neutra: un 0% en rojo diría que algo está mal cuando
   lo que pasa es que todavía no hay nada declarado. */
.kl-meter-empty {
  background: var(--kl-border-soft);
}
.kl-meter-popover {
  max-height: 14rem;
  overflow-y: auto;
}
.kl-meter-popover-title {
  font-weight: 600;
  margin-bottom: 2px;
}
.usage-none {
  color: var(--kl-text-muted);
}
.usage-none-unused {
  color: #b02a37;
}
.usage-unused-row {
  background-color: rgba(220, 53, 69, 0.05);
}
.helper {
  cursor: help;
}
.unit-row td {
  background-color: var(--kl-color-accent-soft, #eef3f3);
}
.unit-row-clickable td {
  cursor: pointer;
  user-select: none;
}
.unit-row-clickable:hover td {
  background-color: #e2ebeb;
}
.unit-title {
  font-weight: 600;
  font-size: var(--kl-fs-body);
}
.usage-toggle-spacer {
  display: inline-block;
  width: 1.6rem;
  flex-shrink: 0;
}
.indicator-cell {
  padding-left: 1.4rem;
}
.usage-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  margin-right: 0.45rem;
  flex-shrink: 0;
  color: var(--kl-color-accent-medium, #2361a0);
}
.coherence-row td {
  background-color: #f7f9f9;
}
.coherence-box {
  padding: 0.5rem 0.75rem;
}
.coherence-flag {
  font-size: var(--kl-fs-small);
  margin-bottom: 0.35rem;
}
.coherence-flag-danger {
  color: #b02a37;
}
.coherence-flag-warning {
  color: #8a6100;
}
.coherence-flag-ok {
  color: #1d6b3f;
}
.coherence-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.75rem;
  margin-top: 0.5rem;
}
@media (max-width: 900px) {
  .coherence-columns {
    grid-template-columns: minmax(0, 1fr);
  }
}
.coherence-side-title {
  font-size: var(--kl-fs-caption);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  margin-bottom: 0.5rem;
}
.coherence-piece {
  margin-bottom: 0.6rem;
  padding-left: 0.5rem;
  border-left: 2px solid var(--kl-chip-border, #b6d9f5);
}
.coherence-piece:last-child {
  margin-bottom: 0;
}
.coherence-piece-name {
  font-size: var(--kl-fs-body);
  font-weight: 600;
  margin-bottom: 0.2rem;
}
.coherence-chip {
  display: inline-block;
  padding: 0.05rem 0.45rem;
  margin: 0.15rem 0.25rem 0 0;
  border-radius: var(--kl-radius-pill);
  border: 1px solid var(--kl-table-border, #d9e0e0);
  font-size: var(--kl-fs-caption);
}
.coherence-piece-row {
  margin-top: 0.15rem;
}
.coherence-row-label {
  font-size: var(--kl-fs-caption);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #000;
  font-weight: 700;
  margin-right: 0.3rem;
}
.coherence-missing {
  color: #b02a37;
  font-size: var(--kl-fs-small);
}
.coherence-chip-shared {
  border-color: #1d6b3f;
  color: #1d6b3f;
  font-weight: 600;
}
/* E49 · Los momentos de la Unidad. Misma píldora que las estrategias y las evidencias
   —se leen en la misma zona y son la misma clase de dato—, con el punto de color del
   momento y el conteo al final para que el número no se confunda con el nombre. */
.kl-unit-moments {
  margin-top: 0.15rem;
  margin-bottom: 0.35rem;
}
.kl-unit-moment-chip {
  padding-left: 0.25rem;
}
.kl-unit-moment-dot {
  width: 0.55rem;
  height: 0.55rem;
  margin: 0 0.25rem 0 0;
}
.kl-unit-moment-count {
  font-weight: var(--kl-fw-semibold, 600);
  font-variant-numeric: tabular-nums;
  margin-left: 0.3rem;
}
.kl-unit-moment-loose {
  color: var(--kl-text-muted);
  font-style: italic;
}
/* El momento que la Unidad no trabaja. Mismo rojo que el resto de los faltantes de este
   desplegable (`coherence-missing`, las banderas de peligro): en esta tabla el rojo ya
   significa «acá falta algo», así que el hueco del modelo se lee igual que los otros. */
.kl-unit-moment-empty {
  color: #b02a37;
  border-color: #b02a37;
}
.kl-unit-moment-empty .kl-unit-moment-dot {
  background-color: transparent;
  border: 1px solid #b02a37;
}
.coherence-none {
  color: var(--kl-text-muted);
  font-style: italic;
  font-size: var(--kl-fs-small);
}
/* E48 · El botón de IA de la cabecera. Mismo color y mismo ícono que los de la Matriz 2
   —es la misma acción en otra tabla— y en tamaño chico para que el título siga siendo
   lo primero que se lee. */
.kl-triangulation-ai {
  margin-left: 0.75rem;
  vertical-align: middle;
  font-size: var(--kl-fs-small);
}
.icon-btn-ai {
  width: 16px;
  height: 16px;
}
::v-deep .btn-ai-custom {
  background-color: var(--kl-fourth-hover-color) !important;
  border-color: var(--kl-fourth-hover-color) !important;
  color: white !important;
}
::v-deep .btn-ai-custom:hover,
::v-deep .btn-ai-custom:focus,
::v-deep .btn-ai-custom:active {
  background-color: var(--kl-fourth-hover-color) !important;
  border-color: var(--kl-fourth-hover-color) !important;
  opacity: 0.9;
}
/* E46 · La distribución por momento. Misma familia visual que los medidores de arriba
   pero con la barra más fina: no es un indicador más de cobertura, es la forma del
   diseño. El `text-align: left` explícito en cada parte, igual que en `.kl-meter-label`
   y `.kl-stat`: la vista centra el texto más arriba y cada bloque lo desarma por su
   cuenta. */
.kl-moment-dist-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: var(--kl-fs-small);
  line-height: 1.5;
}
.kl-moment-dist-name {
  flex: 0 0 11rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}
.kl-moment-dist-loose {
  color: var(--kl-text-muted);
  font-style: italic;
}
.kl-moment-dist-dot {
  margin-left: 0;
}
.kl-moment-dist-track {
  flex: 1 1 auto;
  height: 0.35rem;
  border-radius: 2px;
  background-color: #eef1f0;
  overflow: hidden;
}
.kl-moment-dist-fill {
  display: block;
  height: 100%;
  border-radius: 2px;
}
/* La cifra va tabular y con ancho fijo para que las barras arranquen y terminen alineadas
   aunque los conteos tengan distinta cantidad de dígitos. */
.kl-moment-dist-count {
  flex: 0 0 1.6rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: var(--kl-fw-semibold);
}
/* El desglose cierra la fila y no envuelve: si no cabe, antes se angosta la barra —que
   sólo compara— que partir el texto en dos líneas. */
.kl-moment-dist-split {
  flex: 0 0 auto;
  white-space: nowrap;
  font-size: var(--kl-fs-caption);
  color: var(--kl-text-muted);
  text-align: left;
}
.moment-dot {
  display: inline-block;
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 50%;
  margin: 0 0.4rem 0 0.5rem;
  vertical-align: middle;
}
@media print {
  .badge {
    border: none;
  }
}
</style>
